import React, { useState } from 'react';
import type { MediaItem } from '../../../types/animatronic';
import MediaCredit from '../MediaCredit';
import LoadingState from '../../ui/LoadingState';

interface MediaViewerProps {
  item: MediaItem;
  name: string;
}

const MediaViewer: React.FC<MediaViewerProps> = ({ item, name }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [useDirectVideo, setUseDirectVideo] = useState(false);

  // Extract Cloudinary video ID and create proper embed URL
  const getCloudinaryEmbedUrl = (url: string) => {
    if (url.includes('cloudinary.com')) {
      // Handle full iframe code or existing player URL
      if (url.includes('player.cloudinary.com/embed')) {
        return url;
      }
      
      // Handle direct video URL
      const matches = url.match(/https:\/\/res\.cloudinary\.com\/([^\/]+)\/video\/upload\/(.+)/);
      if (matches && matches.length === 3) {
        const [_, cloudName, videoPath] = matches;
        const publicId = videoPath.split('.')[0];
        
        // Add optimized parameters for better compatibility
        return `https://player.cloudinary.com/embed/?public_id=${publicId}&cloud_name=${cloudName}&player[controls]=true&player[colors][base]=%233ECF8E&player[colors][accent]=%233ECF8E&player[colors][text]=%23FFFFFF&player[fluid]=true&player[muted]=false&source[sourceTypes][0]=mp4&source[sourceTypes][1]=webm&profile=Database_Video_V1`;
      }
    }
    return url;
  };

  if (error) {
    return (
      <div className="aspect-video bg-background-secondary rounded-lg flex items-center justify-center">
        <p className="text-content-secondary">Failed to load media</p>
      </div>
    );
  }

  return (
    <div className="relative aspect-video rounded-lg overflow-hidden bg-background-secondary group">
      {isLoading && <LoadingState />}
      
      {/* Credit overlay */}
      {item.credit && (
        <div className="absolute top-2 left-2 bg-black/50 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity z-10">
          <MediaCredit credit={item.credit} creditUrl={item.credit_url} />
        </div>
      )}
      
      {item.type === 'video' ? (
        useDirectVideo ? (
          // Direct video element as fallback
          <video
            src={item.url}
            controls
            playsInline
            preload="metadata"
            className="w-full h-full"
            onLoadedData={() => setIsLoading(false)}
            onError={() => {
              console.error('Failed to load video:', item.url);
              setError('Failed to load video');
              setIsLoading(false);
            }}
          >
            <source src={item.url} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        ) : (
          // Try Cloudinary player first
          <iframe
            src={getCloudinaryEmbedUrl(item.url)}
            title={`${name} video`}
            className="w-full h-full"
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
            frameBorder="0"
            onLoad={() => setIsLoading(false)}
            onError={() => {
              console.log('Falling back to direct video player');
              setUseDirectVideo(true);
            }}
          />
        )
      ) : (
        <img
          src={item.url}
          alt={name}
          className={`w-full h-full object-contain transition-opacity duration-300 ${
            isLoading ? 'opacity-0' : 'opacity-100'
          }`}
          loading="lazy"
          onLoad={() => setIsLoading(false)}
          onError={() => {
            console.error('Failed to load image:', item.url);
            setError('Failed to load image');
            setIsLoading(false);
          }}
        />
      )}
    </div>
  );
};

export default MediaViewer;